import * as React from 'react';

import cn from 'classnames';
import { findIndex } from 'lodash';

import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';

import { ProductSlider } from '@components/ProductSlider';
import { useGetProductItemProps } from '@components/Universals/ProductItem/useGetProductItemProps';
import { IProduct } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import {
  useTrackDynamicList,
  ProductEventWither,
} from '@helpers/googleTagManager';

import { ProductsSectionPlaceholder } from '../ProductsSectionPlaceholder';

import { BULK_CALL_TIMEOUT } from './constants';
import {
  legacyDiscoveryCardClass,
  newDiscoveryCardClass,
  SliderWrapper,
} from './styled';

export interface IProductSectionContentProps {
  listLabel?: string;
  trackProductClick?: boolean;
  trackProductImpression?: boolean;
  data: GetProductsByIdBatchedQuery;
  itemsPerSlide: number;
  attributionToken?: string;
  className?: string;
  isSponsored?: boolean;
  onProductClick?: (productId: string) => void;
  onProductImpression?: (productId: string) => void;
}

export const ProductsSectionContent: React.FC<IProductSectionContentProps> =
  React.memo(
    ({
      listLabel = '',
      trackProductClick = false,
      trackProductImpression = false,
      data,
      itemsPerSlide,
      attributionToken,
      className,
      isSponsored = false,
      onProductClick,
      onProductImpression,
    }) => {
      const trackVisibleItems = useTrackDynamicList({
        delay: BULK_CALL_TIMEOUT,
        listName: listLabel,
      });

      const { release_discovery_product_card_2026 } = useFeatureFlags();

      const onNewItemShow = (id: IProduct['id']) => {
        if (trackProductImpression && listLabel) {
          const index = findIndex(data.vpProductByIds, { id });
          if (index !== -1) {
            const product = data.vpProductByIds[index];
            if (!product) return;
            trackVisibleItems(
              ProductEventWither()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .withVpProduct(product as any)
                .withDiscountPrices(product.attributes, {
                  shouldSendOnlyDiscountPrice: true,
                })
                .withAdditionalData({
                  list_position: index + 1,
                  id: product.id ?? '',
                  list_name: isSponsored ? `${listLabel}_sponsored` : listLabel,
                })
                .build()
            );
          }
        }
        onProductImpression?.(id);
      };

      const getProductItemProps = useGetProductItemProps({
        noPriceIfUnavailable: true,
        inSectionOfType: 'slider',
        GTMEventLabel: isSponsored ? `${listLabel}_sponsored` : listLabel,
        sendGTM: trackProductClick,
        wishlistActionLocation: 'list',
      });

      const products = data.vpProductByIds
        .filter(
          (product): product is NonNullable<typeof product> => product !== null
        )
        .map((product, index) => {
          const props = getProductItemProps({
            product,
            position: index + 1,
            attributionToken,
            isSponsored,
          });
          if (!props || !onProductClick) return props;
          const originalOnClick = props.onClick;
          return {
            ...props,
            onClick: (
              ...args: Parameters<NonNullable<typeof originalOnClick>>
            ) => {
              onProductClick(product.id!);
              originalOnClick?.(...args);
            },
          };
        })
        .filter((p): p is NonNullable<typeof p> => Boolean(p));

      return (
        <SliderWrapper
          className={cn(
            className,
            release_discovery_product_card_2026
              ? newDiscoveryCardClass
              : legacyDiscoveryCardClass
          )}
        >
          <ProductSlider
            itemsPerSlide={itemsPerSlide}
            inViewThreshold={0.55}
            onItemShow={({ product }) => {
              onNewItemShow(product.id);
            }}
            products={products}
            fallback={
              <ProductsSectionPlaceholder
                itemsPerSlide={itemsPerSlide}
                listLabel={listLabel}
              />
            }
          />
        </SliderWrapper>
      );
    }
  );
